/**
 * Leitura de um par da gaveta "Revisão de duplicatas" (Licitações > Encontrar).
 *
 * ⛔ QUEM SOBREVIVE NÃO É DECISÃO DA TELA. O backend devolve os pares
 * ordenados por `a.id < b.id` e, até 10/2026, não escolhia sobrevivente: o
 * "⭐ sobrevive" pré-marcado era um tie-break inventado aqui (CNPJ, depois
 * menor id). O operador acabava CONFIRMANDO a escolha da interface em vez de
 * escolher, justamente nos pares ambíguos, onde ele deveria pensar.
 *
 * Pior: a regra do servidor (mergeGroup) é score de atividade (proposta 8,
 * disputa 4, monitorada 2, status 1, mensagem 1) e SÓ NO EMPATE CNPJ e id
 * menor. A tela usava apenas essa cauda. Num par em que um lado tem proposta e
 * o outro tem CNPJ, a tela pré-marcava o do CNPJ e, confirmado, mandava esse id
 * como survivorId, que FORÇA por cima do score: apagava o lado da proposta
 * (achado do Bastião, 10/2026).
 *
 * Agora o lado sai do servidor. `survivorDecidedBy` diz COMO ele escolheu:
 * - 'atividade': um lado tem mais atividade. Chega pré-marcado.
 * - 'desempate': score empatado, decidido por CNPJ/id, que é sorteio e não
 *   julgamento. Chega NEUTRO, e o "Juntar" só habilita após a escolha.
 * Campo ausente (backend antigo) também chega neutro: na dúvida, o operador
 * escolhe. A pergunta "é a mesma licitação?" é outra, e é o operador que
 * responde ao juntar ou dispensar.
 *
 * ⚠️ O PERCENTUAL INFORMA, NÃO DECIDE. Nos casos de truncamento ele mede
 * comprimento de string, não conteúdo (medição da Bússola, 10/2026): 63% foi
 * duplicata real (Xanxerê) e 53% foi falso positivo (Porangatu). E no par de
 * objeto curto (o BLL grava resumo de uma linha, 48 caracteres contra 594 da
 * mesma licitação), 4% não significa nada. Por isso o número não tem peso
 * visual de veredito, e no objeto curto ele dá lugar ao motivo.
 */

export type Lado = 'a' | 'b';

export interface ParDeDuplicata {
  a: any;
  b: any;
  similarity: number;
  /** Por qual passo/portão o par entrou na fila. */
  reason?: string | null;
  /** Lado com objeto curto, quando reason = 'objeto_curto'. */
  shortSide?: Lado | null;
  /** Containment do objeto (no objeto_curto, é o número que mede algo). */
  containment?: number | null;
  /** Como o servidor escolheu o sobrevivente sugerido. */
  survivorDecidedBy?: 'atividade' | 'desempate' | string | null;
  /** Sobrevivente pela regra inteira do mergeGroup (score incluído). */
  suggestedSurvivorId?: number | null;
}

export interface LeituraDoPar {
  /** Lado pré-marcado (só com survivorDecidedBy = 'atividade'); null = neutro. */
  ladoPadrao: Lado | null;
  objetoCurto: boolean;
  ladoCurto: Lado | null;
  /** Texto do cabeçalho do par: motivo, ou o percentual com o devido peso. */
  rotulo: string;
  /** Explicação do rótulo (title). */
  explicacao: string;
}

const tamanhoDoObjeto = (s: any) => String(s?.objetoCompra || '').trim().length;

export function lerPar(par: ParDeDuplicata): LeituraDoPar {
  const objetoCurto = par.reason === 'objeto_curto';
  let ladoCurto: Lado | null = null;
  if (objetoCurto) {
    ladoCurto =
      par.shortSide === 'a' || par.shortSide === 'b'
        ? par.shortSide
        : tamanhoDoObjeto(par.a) <= tamanhoDoObjeto(par.b) ? 'a' : 'b';
  }

  // Objeto curto nunca pré-marca: o próprio par é incerto.
  const decidido = par.survivorDecidedBy === 'atividade' && !objetoCurto;
  let ladoPadrao: Lado | null = null;
  if (decidido && par.suggestedSurvivorId != null) {
    if (par.suggestedSurvivorId === par.a?.id) ladoPadrao = 'a';
    else if (par.suggestedSurvivorId === par.b?.id) ladoPadrao = 'b';
  }
  const pct = Math.round((Number(par.similarity) || 0) * 100);
  const rotulo = objetoCurto ? 'Objeto curto num dos lados' : `objeto ${pct}% parecido`;
  const cont = par.containment != null ? ` Containment: ${Math.round(Number(par.containment) * 100)}%.` : '';
  const explicacao = objetoCurto
    ? `Um dos portais grava só um resumo de uma linha do objeto. A similaridade (${pct}%) mede o tamanho do texto, não o conteúdo: compare órgão, número, data e valor.${cont}`
    : 'Similaridade do texto do objeto. Informa, não decide: texto truncado por um portal derruba o número mesmo quando é a mesma licitação.';

  return { ladoPadrao, objetoCurto, ladoCurto, rotulo, explicacao };
}

/** Lado que sobrevive: a escolha do operador vence; sem ela, o padrão (ou nenhum). */
export function ladoEscolhido(leitura: LeituraDoPar, escolha: Lado | undefined): Lado | null {
  return escolha ?? leitura.ladoPadrao;
}

/**
 * O que vai no POST merge-duplicate. `survivorId` só quando o OPERADOR clicou
 * num lado: ele força por cima do score. Pré-marcado e confirmado sem clique
 * vai sem, e o servidor decide pela regra inteira (dá o mesmo lado).
 */
export function pedidoDeJuntar(par: ParDeDuplicata, lado: Lado, escolhaDoOperador: boolean) {
  const fica = lado === 'a' ? par.a.id : par.b.id;
  const sai = lado === 'a' ? par.b.id : par.a.id;
  return { itemId: fica, otherId: sai, survivorId: escolhaDoOperador ? fica : undefined };
}
